import { useRef, useState } from 'preact/hooks';
import { api } from '../api';
import type { OpmlImportResult } from '../../shared/types';

interface OpmlManagerProps {
  onImportComplete?: () => void;
}

export function OpmlManager({ onImportComplete }: OpmlManagerProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OpmlImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    setBusy(true);
    setError(null);
    setResult(null);

    try {
      const text = await file.text();
      if (!text.trim()) {
        throw new Error('The selected OPML file is empty.');
      }
      const res = await api.importOpml(text);
      setResult(res);
      if (onImportComplete && res.imported > 0) {
        onImportComplete();
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to import OPML file.');
    } finally {
      setBusy(false);
      // Reset input value so re-uploading the same file works
      target.value = '';
    }
  };

  const handleExport = () => {
    const url = api.exportOpmlUrl();
    const a = document.createElement('a');
    a.href = url;
    a.download = 'podcastie-subscriptions.opml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div class="opml-manager">
      <div class="stack stack--row">
        <input
          ref={fileInputRef}
          type="file"
          accept=".opml,.xml,text/xml,application/xml"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          disabled={busy}
        />
        <button
          type="button"
          class="button button--ghost"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
        >
          {busy ? 'Importing…' : 'Import OPML'}
        </button>
        <button
          type="button"
          class="button button--ghost"
          onClick={handleExport}
          disabled={busy}
        >
          Export OPML
        </button>
      </div>

      {error && <p class="error" style={{ marginTop: '0.75rem' }}>{error}</p>}

      {result && (
        <div class="notice" style={{ marginTop: '0.75rem' }}>
          <p>
            <strong>Import complete:</strong> {result.imported} new podcast
            {result.imported === 1 ? '' : 's'} added
            {result.alreadySubscribed > 0
              ? `, ${result.alreadySubscribed} already in library`
              : ''}
            {result.failed > 0 ? `, ${result.failed} failed` : ''}.
          </p>
          {result.failed > 0 && (
            <details style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
              <summary>View failed items ({result.failed})</summary>
              <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0 }}>
                {result.results
                  .filter((r) => r.status === 'failed')
                  .map((r, idx) => (
                    <li key={idx}>
                      {r.title ? <strong>{r.title}</strong> : r.url}: {r.error}
                    </li>
                  ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
